export const GLYPH_FAMILIES = ["lens", "code", "signal", "flow", "dialog", "spark"] as const;
export type GlyphFamily = typeof GLYPH_FAMILIES[number];
export const GLYPH_VARIANTS = 1024;

function hash(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
  return h >>> 0;
}

export function glyphFamily(seed: string): GlyphFamily {
  const explicit = seed.split(":");
  const family = explicit[0] === "mark" ? explicit[2] : explicit[0] === "glyph" ? explicit[1] : "";
  if (GLYPH_FAMILIES.includes(family as GlyphFamily)) return family as GlyphFamily;
  if (/research|scout|fact|search|analyst/i.test(seed)) return "lens";
  if (/code|develop|engineer|schema|build/i.test(seed)) return "code";
  if (/data|chart|metric|analytic|finance/i.test(seed)) return "signal";
  if (/flow|automat|plan|trip|task|ops/i.test(seed)) return "flow";
  if (/support|writ|edit|copy|sales|chat|communicat/i.test(seed)) return "dialog";
  return GLYPH_FAMILIES[hash(seed) % GLYPH_FAMILIES.length];
}

export function glyphKey(seed: string): string {
  if (/^mark:v1:(lens|code|signal|flow|dialog|spark):(0|[1-9]\d{0,3})$/.test(seed)
    && Number(seed.split(":")[3]) < GLYPH_VARIANTS) return seed;
  return `mark:v1:${glyphFamily(seed)}:${hash(seed) % GLYPH_VARIANTS}`;
}

export function glyphSeed(family: GlyphFamily = GLYPH_FAMILIES[Math.floor(Math.random() * GLYPH_FAMILIES.length)], variation: string = crypto.randomUUID()): string {
  return `mark:v1:${family}:${hash(variation) % GLYPH_VARIANTS}`;
}

/** Exact recipe collisions are checked in previews and again transactionally by the API. */
export function uniqueGlyphSeed(seed: string, occupied: readonly string[]): string {
  const used = new Set(occupied.map(glyphKey));
  const key = glyphKey(seed);
  if (!used.has(key)) return key;
  const family = glyphFamily(key);
  const start = Number(key.split(":")[3]);
  const families = [family, ...GLYPH_FAMILIES.filter((item) => item !== family)];
  for (const item of families) {
    for (let offset = 1; offset <= GLYPH_VARIANTS; offset++) {
      const candidate = `mark:v1:${item}:${(start + offset * 137) % GLYPH_VARIANTS}`;
      if (!used.has(candidate)) return candidate;
    }
  }
  throw new Error("All agent avatar combinations in this workspace are in use.");
}

export function creaturePalette(seed: string, hue?: number): { hue: number; saturation: number; lightness: number } {
  const n = Number(glyphKey(seed).split(":")[3]);
  return { hue: ((hue ?? hash(seed) % 360) % 360 + 360) % 360, saturation: 68 + (n % 8) * 3, lightness: 52 + ((n >>> 3) % 8) * 2 };
}

export interface CreatureIdentity { seed: string; hue: number }

export function paletteKey(seed: string, hue: number): string {
  const p = creaturePalette(seed, hue);
  const s = p.saturation / 100, l = p.lightness / 100;
  const a = s * Math.min(l, 1 - l);
  const channel = (n: number) => {
    const k = (n + p.hue / 30) % 12;
    return Math.round(255 * (l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))));
  };
  return [channel(0), channel(8), channel(4)].join(",");
}

export function uniqueCreature(seed: string, hue: number, occupied: readonly CreatureIdentity[]): CreatureIdentity {
  const blocked = occupied.map((item) => item.seed);
  const colors = new Set(occupied.map((item) => paletteKey(item.seed, item.hue)));
  const hues = new Set(occupied.map((item) => item.hue));
  const distance = (h: number) => Math.min(360, ...[...hues].map((used) => Math.min(Math.abs(h - used), 360 - Math.abs(h - used))));
  const candidates = Array.from({ length: 360 }, (_, i) => (hue + i * 137) % 360);
  const distances = new Map(candidates.map((candidate) => [candidate, distance(candidate)]));
  candidates.sort((a, b) => Number(hues.has(a)) - Number(hues.has(b)) || distances.get(b)! - distances.get(a)!);
  for (;;) {
    seed = uniqueGlyphSeed(seed, blocked);
    if (!hues.has(hue) && !colors.has(paletteKey(seed, hue))) return { seed, hue };
    for (const candidate of candidates) {
      if (!colors.has(paletteKey(seed, candidate))) return { seed, hue: candidate };
    }
    blocked.push(seed);
  }
}
